import { useMutation } from '@apollo/client';
import { experimentFeatureFlagDefaults, type ExperimentFeatureFlags } from '@cosuno/feature-flags';
import { snakeCase } from 'lodash';
import { useCallback, useContext, useMemo } from 'react';
import { assert } from 'ts-essentials';

import type { EXPERIMENT_NAME } from '#@/__gql__/graphql';
import {
  trackBidPackageExperimentMetricMutation,
  trackExperimentImpressionMutation,
  trackExperimentMetricMutation,
} from '#@/api';

import { ExperimentFlagsContext, PROVIDER_DOESNT_EXIST } from '../context';

export type TrackExperimentImpressionFunc = (
  experimentFlagName: keyof ExperimentFeatureFlags,
) => Promise<void>;

export type TrackExperimentMetricFunc = (metricName: string) => Promise<void>;
export type TrackBidPackageExperimentMetricFunc = (
  bidPackageId: string,
  metricName: string,
) => Promise<void>;

/**
 * Use to get feature flag values from LaunchDarkly. It may only be used inside
 * of `ExperimentFlagsProvider`.
 */
export const useExperimentFlags = (): ExperimentFeatureFlags & {
  trackExperimentImpression: TrackExperimentImpressionFunc;
  trackExperimentMetric: TrackExperimentMetricFunc;
  trackBidPackageExperimentMetric: TrackBidPackageExperimentMetricFunc;
} => {
  const featureFlags = useContext(ExperimentFlagsContext);

  assert(
    featureFlags !== PROVIDER_DOESNT_EXIST,
    '`useExperimentFlags` must be used inside of a `ExperimentFeatureFlagsContext`.',
  );

  const [trackImpressionMutation] = useMutation(trackExperimentImpressionMutation);

  const [trackMetricMutation] = useMutation(trackExperimentMetricMutation);
  const [trackBidPackageMetricMutation] = useMutation(trackBidPackageExperimentMetricMutation);

  const trackExperimentImpression: TrackExperimentImpressionFunc = useCallback(
    async (experimentFlagName) => {
      try {
        await trackImpressionMutation({
          variables: { experimentFlagName: snakeCase(experimentFlagName) as EXPERIMENT_NAME },
        });
      } catch (e) {
        // swallow everything
      }
    },
    [trackImpressionMutation],
  );

  const trackExperimentMetric: TrackExperimentMetricFunc = useCallback(
    async (metricName) => {
      try {
        await trackMetricMutation({
          variables: { metricName },
        });
      } catch (e) {
        // swallow everything
      }
    },
    [trackMetricMutation],
  );

  const trackBidPackageExperimentMetric: TrackBidPackageExperimentMetricFunc = useCallback(
    async (bidPackageId, metricName) => {
      try {
        await trackBidPackageMetricMutation({
          variables: { bidPackageId, metricName },
        });
      } catch (e) {
        // swallow everything
      }
    },
    [trackBidPackageMetricMutation],
  );

  return useMemo(
    () => ({
      ...experimentFeatureFlagDefaults,
      ...featureFlags,
      trackExperimentImpression,
      trackExperimentMetric,
      trackBidPackageExperimentMetric,
    }),
    [
      featureFlags,
      trackExperimentImpression,
      trackExperimentMetric,
      trackBidPackageExperimentMetric,
    ],
  );
};
